/**
 * App — M1 core loop della sessione (build plan §3): Oggi (template di oggi) → Inizia →
 * log set (input D18 ±/tap RIR + pre-fill D20 come PROPOSTA etichettata D51) → rest timer D19
 * (automatico, salta/estendi 1 tap) → correggi ultimo set (D52, ≤2 tap, = `set.updated` su
 * `set_id`) → review finale → sessione nel Log → export sessione JSONL+MD (D3).
 *
 * Regole che governano questo file: contratto = verità (§2.2/§2.3, nessun campo inventato) ·
 * lo stato si DERIVA dagli eventi (proiezione.ts: mai una verità parallela) · testo è
 * l'interfaccia (tabelle, niente card) · colori solo token (B11) · stringhe solo i18n (D11) ·
 * errori = status line in italiano, mai eccezioni nude (regola 1) · ogni azione ≤ 2 tap (D18).
 */
import { createSignal, createMemo, onMount, onCleanup, For, Show } from "solid-js";import { makeEvent, nextSetNumber, parseEvent, isTombstone, SCHEMA_VERSION, type HyprEvent } from "./storage/events";
import { appendEvents, listEvents, initDb, storageAvailable } from "./storage/db";
import { newId } from "./storage/ids";
import { migraEventi } from "./storage/migrazioni";
import { eventiDaImportare, esitoIdentita } from "./lib/merge";
import { jsonlFile, canShareFile, downloadText, EXPORT_MIME } from "./lib/export";
import { jsonlSessione, markdownSessione, nomeFileSessione, caricoLeggibile } from "./lib/export-md";
import {
  statiSet,
  sessioni,
  sessioneAttiva,
  statoSessione,
  ultimoSetEsercizio,
  campiModificati,
  metricaIdenticiUltimo,
  formatoResto,
  displayName,
  type SetStato,
} from "./lib/proiezione";
import {
  GIORNI,
  giornoDellaData,
  nomeEsercizioPerGiorno,
  prescrizione,
  caricoDiDefault,
  type EsercizioTemplate,
  type GiornoTemplate,
} from "./data/programma";
import { persistStatus, requestPersist, opfsDataBytes, type PersistStatus } from "./storage/persist";
import { perfNumbers, perfRt, perfDiag, measureReaction, PERF_SUPPORT } from "./lib/perf";
import { clsDisplay, inpDisplay, lcpDisplay, type MetricDisplay } from "./lib/perf-state";
import { t } from "./i18n/it";

const USER_KEY = "hypr.user_id";
const DEVICE_KEY = "hypr.device_id";

// Versione build = hash dei contenuti iniettato da scripts/build-sw.mjs: STESSO identificativo
// della cache del service worker → "vedo la versione nuova" = "l'aggiornamento è arrivato".
const BUILD_VERSION =
  document.querySelector('meta[name="app-version"]')?.getAttribute("content") ?? t("unknown");

const OGGI_ISO_CALCOLA = (): string => new Date().toISOString().slice(0, 10);

type TipoCarico = "barbell" | "bodyweight" | "bodyweight_added";
const TIPI_CARICO: readonly TipoCarico[] = ["barbell", "bodyweight", "bodyweight_added"];

export function App() {
  const [events, setEvents] = createSignal<HyprEvent[]>([]);
  const [status, setStatus] = createSignal<PersistStatus | null>(null);
  const [dataBytes, setDataBytes] = createSignal<number | null>(null); // byte REALI (walk OPFS), non la stima piattaforma
  const [storageMode, setStorageMode] = createSignal(t("unknown")); // opfs-sahpool | memory: ispezionabile, mai magia
  const [note, setNote] = createSignal("");
  const [ready, setReady] = createSignal(false);
  const [busy, setBusy] = createSignal(false); // un'azione scrittura alla volta (anti-race: v. salva)
  const [swOn, setSwOn] = createSignal(t("no")); // il service worker controlla la pagina?

  // ---- navigazione (D17: 3 tab; la Sessione è una vista con "◀" di ritorno) ----
  const [vista, setVista] = createSignal<"oggi" | "sessione" | "log" | "account">("oggi");

  // ---- input set (D18) ----
  const [esercizioIdx, setEsercizioIdx] = createSignal(0);
  const [reps, setReps] = createSignal(0);
  const [carico, setCarico] = createSignal(0);
  // Fallback tastiera (D18 opzione a: "±bottoni + tastiera numerica … per precisione"): il DATO
  // resta il numero nei signal; i buffer testuali servono solo a non riscrivere mentre si digita
  // e si riallineano a ogni modifica esterna (±, pre-fill, correzione) — mai una verità parallela.
  const [repsTesto, setRepsTesto] = createSignal("0");
  const [caricoTesto, setCaricoTesto] = createSignal("0");
  const [tipoCarico, setTipoCarico] = createSignal<TipoCarico>("barbell");
  const [rir, setRir] = createSignal<number | null>(null); // tap 0-4: mai pre-compilato (D51)
  const [noteSet, setNoteSet] = createSignal("");
  const [warmup, setWarmup] = createSignal(false);
  const [proposta, setProposta] = createSignal<{ reps: number; carico: number; tipo: TipoCarico; fonte: string } | null>(null);
  const [correzioneId, setCorrezioneId] = createSignal<string | null>(null); // D52
  const [reviewAperta, setReviewAperta] = createSignal(false); // D52: review a fine sessione
  const [noteSessione, setNoteSessione] = createSignal("");
  const [nomeUtente, setNomeUtente] = createSignal(""); // D54: display_name, edit inline in Account

  // ---- contesto sessione (wireframe §1: peso/sonno/energia, edit inline) ----
  const [peso, setPeso] = createSignal("");
  const [sonno, setSonno] = createSignal("");
  const [energia, setEnergia] = createSignal<number | null>(null);

  // ---- rest timer (D19: automatico dopo Salva set) ----
  const [restFino, setRestFino] = createSignal<number | null>(null);
  const [ora, setOra] = createSignal(Date.now());

  // Footer bar (nav + status line) FISSA: il contenuto riserva lo spazio REALE che occupa,
  // misurato con ResizeObserver — non indovinato. Con la status line a campi larghi (D44 CLS)
  // l'altezza varia col viewport (273px su iPhone 390): la riserva fissa a 172px copriva il CTA
  // "Riprendi sessione" (segnalazione device 2026-09-26). Cambiare il padding-bottom NON muove
  // il contenuto sopra (solo l'estensione di scroll): nessun CLS, gate G-perf invariato.
  let footerEl: HTMLDivElement | undefined;

  // Data di oggi: MAI congelata al load del modulo — un'app Home Screen tenuta viva oltre
  // mezzanotte deve mostrare e scrivere la data giusta, non quella di ieri (blind review M1).
  // Si ricalcola al tick del timer: la mezzanotte non è un caso speciale.
  const dataOggi = createMemo(() => {
    ora();
    return OGGI_ISO_CALCOLA();
  });
  const slotOggi = createMemo(() => giornoDellaData(dataOggi()));
  const attiva = createMemo(() => sessioneAttiva(events()));
  const elencoSessioni = createMemo(() => sessioni(events()));
  const setsSessione = createMemo(() => {
    const s = attiva();
    return s ? statiSet(events(), s.session_id) : [];
  });

  // Il template della sessione = il giorno del suo day_label (una sessione iniziata ieri resta
  // il suo Day: mai rimbalzi di template). Fallback = slot di oggi (dati storici con label ignote).
  const giornoSessione = createMemo<GiornoTemplate | null>(() => {
    const s = attiva();
    if (!s) return null;
    return GIORNI.find((g) => g.day_label === s.day_label) ?? slotOggi()?.giorno ?? null;
  });
  const esercizioCorrente = createMemo<EsercizioTemplate | null>(() => {
    const g = giornoSessione();
    if (!g) return null;
    return g.esercizi[esercizioIdx()] ?? g.esercizi[g.esercizi.length - 1] ?? null;
  });

  function id(key: string, make: () => string): string {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const fresh = make();
    localStorage.setItem(key, fresh);
    return fresh;
  }

  async function refresh(): Promise<void> {
    // M2: la migrazione è un UPCAST in lettura (§2.3: il log non si riscrive mai) — anche il DB
    // del device con eventi PRE-fix (set senza session_id, campi D48 assenti) si riallinea qui.
    // Funzione pura, idempotente: migrare due volte = migrare una (tests/migrazioni.test.ts).
    setEvents(migraEventi(await listEvents()).eventi);
    setStatus(await persistStatus());
    setDataBytes(await opfsDataBytes());
    // D54: il nome visualizzato si DERIVA dagli eventi a ogni refresh (mai un draft nascosto:
    // uno scrittore esterno/merge deve riallineare la UI senza magia)
    const n = displayName(events());
    if (n !== "") setNomeUtente(n);
  }

  onMount(() => {
    if (footerEl) {
      const misura = (): void => {
        document.body.style.paddingBottom = `${footerEl?.offsetHeight ?? 0}px`;
      };
      misura();
      const osservatore = new ResizeObserver(misura);
      osservatore.observe(footerEl);
      onCleanup(() => osservatore.disconnect());
    }
    // tick del rest timer (D19): un solo interval per l'app, si spegne con l'app
    const tick = setInterval(() => setOra(Date.now()), 500);
    onCleanup(() => clearInterval(tick));
    void avvia();
  });

  async function avvia(): Promise<void> {
    // stato service worker, ispezionabile: se è "no" l'offline non esiste (niente magia)
    if ("serviceWorker" in navigator) {
      if (navigator.serviceWorker.controller) setSwOn(t("yes"));
      navigator.serviceWorker.addEventListener("controllerchange", () => setSwOn(t("yes")));
    }
    try {
      await requestPersist(); // best-effort, subito: è la protezione dall'eviction (WebKit)
      if (!storageAvailable()) {
        setNote(t("storageError"));
      } else {
        const { mode } = await initDb();
        setStorageMode(mode);
      }
      await refresh();
      // ripresa sessione: se una sessione è ancora `in_progress` (kill, chiusura brutale) la UI
      // si riallinea dagli eventi — nessuno stato nascosto, il recupero è la normalità (D2.4)
      const s = attiva();
      if (s) {
        const g = GIORNI.find((x) => x.day_label === s.day_label) ?? slotOggi()?.giorno ?? null;
        const idx = g ? Math.max(0, g.esercizi.findIndex((e) => e.exercise_id === ultimoEsercizioLoggato())) : 0;
        setEsercizioIdx(idx);
        preparaInput();
      }
    } catch (err) {
      // mai eccezione nuda all'utente: l'errore va in status line, in italiano (errorNote)
      setNote(errorNote(err));
    }
    setReady(true);
  }

  /** Esercizio dell'ultimo set loggato nella sessione (per riposizionarsi dopo un kill). */
  function ultimoEsercizioLoggato(): string {
    const sets = setsSessione();
    return sets.length > 0 ? sets[sets.length - 1]!.exercise_id : "";
  }

  /** Scrittura coerente di reps/carico: numero + buffer testuale sempre allineati (D18). */
  function setRepsVal(n: number): void {
    const v = Math.max(0, Math.trunc(n));
    setReps(v);
    setRepsTesto(String(v));
  }

  function setCaricoVal(n: number): void {
    const v = Math.max(0, Math.round(n * 100) / 100); // 2.5kg: mai floating point in status/export
    setCarico(v);
    setCaricoTesto(String(v));
  }

  function digitaReps(testo: string): void {
    setRepsTesto(testo);
    const n = Number.parseInt(testo, 10);
    if (Number.isFinite(n) && n >= 0) setReps(Math.trunc(n));
  }

  function digitaCarico(testo: string): void {
    setCaricoTesto(testo);
    const n = Number.parseFloat(testo.replace(",", "."));
    if (Number.isFinite(n) && n >= 0) setCarico(Math.round(n * 100) / 100);
  }

  /**
   * Pre-fill (D20) come PROPOSTA etichettata (D51): reps+carico dall'ultimo set dell'esercizio
   * (anche da sessioni precedenti) o dal target del template. Il RIR NON si pre-compila: resta
   * il tap dell'utente — è anche per questo che un set tipico sono 2 tap (RIR + salva).
   */
  function preparaInput(): void {
    const ex = esercizioCorrente();
    if (!ex) return;
    setCorrezioneId(null);
    const ultimo = ultimoSetEsercizio(events(), ex.exercise_id);
    if (ultimo) {
      const tipo = TIPI_CARICO.includes(ultimo.load_type as TipoCarico) ? (ultimo.load_type as TipoCarico) : "barbell";
      setRepsVal(ultimo.reps);
      setCaricoVal(ultimo.weight_kg);
      setTipoCarico(tipo);
      setProposta({ reps: ultimo.reps, carico: ultimo.weight_kg, tipo, fonte: t("propostaUltimo") });
    } else {
      const tipo = caricoDiDefault(ex);
      setRepsVal(ex.reps_max);
      setCaricoVal(0);
      setTipoCarico(tipo);
      setProposta({ reps: ex.reps_max, carico: 0, tipo, fonte: t("propostaTemplate") });
    }
    setRir(null);
    setNoteSet("");
    setWarmup(false);
  }

  // D51: la UI distingue il valore PROPOSTO dal valore modificato — mai un pre-fill muto.
  // "Identico" = reps + carico + TIPO carico, come la metrica derivata in proiezione.ts:
  // etichetta e metrica non devono mai raccontare due storie diverse.
  const etichettaProposta = createMemo(() => {
    if (correzioneId() !== null) return t("correzioneInCorso");
    const p = proposta();
    if (p === null) return "";
    return reps() === p.reps && carico() === p.carico && tipoCarico() === p.tipo ? p.fonte : t("modificato");
  });

  function startRest(sec: number): void {
    setRestFino(Date.now() + sec * 1000);
    setOra(Date.now());
  }

  const restLabel = createMemo(() => {
    const fino = restFino();
    return fino === null ? t("restIdle") : formatoResto(fino - ora());
  });

  /**
   * Batch comune: l'account esiste solo quando serve (prima scrittura), mai ID inventati.
   * `nome` vale solo alla CREAZIONE (D54): poi il nome si cambia con `user.updated`.
   * IDENTITÀ (D56): NON è "unico per device" (§1.1) — `id(USER_KEY)` può aver già ricevuto
   * l'identità da un import (bootstrap). Se le identità in gioco sono in conflitto NON si fonde
   * nulla in silenzio: il dialogo "Unisci/Tieni separati" è M5 (D24), fin qui il conflitto si
   * riporta nel resoconto d'import.
   */
  function identita(
    batch: HyprEvent[],
    nome = t("defaultDisplayName"),
  ): { user_id: string; device_id: string; creato: boolean } {
    const user_id = id(USER_KEY, () => newId("u"));
    const device_id = id(DEVICE_KEY, () => newId("dev"));
    const creato = !events().some((e) => e.type === "user.created");
    if (creato) {
      batch.push(
        makeEvent({
          type: "user.created",
          user_id,
          device_id,
          data: { display_name: nome, units: "metric", language: "it", theme: "system" },
        }),
      );
    }
    return { user_id, device_id, creato };
  }

  /** D54: nome visualizzato — edit inline (wireframe §7), `user.updated` = merge-patch §2.3. */
  async function salvaNome(): Promise<void> {
    if (busy()) return;
    setBusy(true);
    try {
      const batch: HyprEvent[] = [];
      const nuovo = nomeUtente().trim();
      // account appena nato: `user.created` porta già il nome scelto, nessun evento in più
      const { user_id, device_id, creato } = identita(batch, nuovo === "" ? t("defaultDisplayName") : nuovo);
      if (!creato && nuovo !== "" && nuovo !== displayName(events())) {
        batch.push(makeEvent({ type: "user.updated", user_id, device_id, data: { display_name: nuovo } }));
      }
      if (batch.length > 0) {
        await appendEvents(batch);
        await refresh();
        setNomeUtente(displayName(events()));
      }
    } catch (err) {
      setNote(errorNote(err));
    } finally {
      setBusy(false);
    }
  }

  /** Inizia sessione: `session.created` (§1.4) col contesto di oggi (wireframe §1). */
  async function iniziaSessione(): Promise<void> {
    if (busy()) return;
    setBusy(true);
    try {
      const batch: HyprEvent[] = [];
      const { user_id, device_id } = identita(batch);
      const g = slotOggi()?.giorno ?? null;
      const data: Record<string, unknown> = {
        date: OGGI_ISO_CALCOLA(),
        day_label: g?.day_label ?? t("sessione"),
        status: "in_progress",
      };
      const pesoKg = Number.parseFloat(peso().replace(",", "."));
      if (Number.isFinite(pesoKg) && peso() !== "") data["weight_kg"] = pesoKg;
      const ore = Number.parseFloat(sonno().replace(",", "."));
      if (Number.isFinite(ore) && sonno() !== "") data["sleep_hours"] = ore;
      const en = energia();
      if (en !== null) data["energy_level"] = en;
      batch.push(
        makeEvent({
          type: "session.created",
          user_id,
          device_id,
          subject: { key: "session_id", value: newId("s") },
          data,
        }),
      );
      await appendEvents(batch);
      await refresh();
      setEsercizioIdx(0);
      preparaInput();
      setReviewAperta(false);
      setNoteSessione("");
      setVista("sessione");
    } catch (err) {
      setNote(errorNote(err));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Salva set (D18) — oppure salva la CORREZIONE dell'ultimo set (D52).
   *
   * ANTI-RACE (blind review M0, CRITICAL 2026-09-25): un tap = un set. Il guard `busy` +
   * pulsante disabilitato serializza le scritture: ogni batch si calcola solo sullo stato già
   * aggiornato dal giro precedente — con due tap ravvicinati si creavano duplicati PERMANENTI
   * nell'append-only (§2.3: gli eventi non si riscrivono).
   *
   * Correzione (D52): è un NUOVO evento `set.updated` che referenzia `set_id` (contratto §2.3
   * merge-patch per-campo) — l'evento errato non si riscrive mai, la UI deriva sempre lo stato
   * corrente (mai "due set" visibili). Il rest timer prosegue (non si riavvia).
   */
  async function salva(avanti: boolean): Promise<void> {
    if (busy()) return;
    setBusy(true);
    try {
      const batch: HyprEvent[] = [];
      const { user_id, device_id } = identita(batch);
      const s = attiva();
      if (!s) return;

      const corrId = correzioneId();
      if (corrId !== null) {
        const base = setsSessione().find((x) => x.set_id === corrId);
        if (!base) return;
        const payload = campiModificati(
          {
            reps: base.reps,
            load_type: base.load_type,
            weight_kg: base.weight_kg,
            rir: base.rir,
            is_warmup: base.is_warmup,
            notes: base.notes,
          },
          {
            reps: reps(),
            load_type: tipoCarico(),
            weight_kg: caricoValore(),
            rir: rir(),
            is_warmup: warmup(),
            notes: noteSet(),
          },
        );
        if (Object.keys(payload).length > 0) {
          batch.push(
            makeEvent({
              type: "set.updated",
              user_id,
              device_id,
              subject: [
                { key: "session_id", value: s.session_id },
                { key: "set_id", value: corrId },
              ],
              data: payload,
            }),
          );
          await appendEvents(batch);
        }
        await refresh();
        setCorrezioneId(null);
        preparaInput();
        return;
      }

      const ex = esercizioCorrente();
      if (!ex) return;
      const data: Record<string, unknown> = {
        exercise_id: ex.exercise_id,
        // progressivo DERIVATO dagli eventi (§2.2): sopravvive al kill
        set_number: nextSetNumber(events(), s.session_id, ex.exercise_id),
        reps: reps(),
        load_type: tipoCarico(),
        weight_kg: caricoValore(),
        is_warmup: warmup(),
        notes: noteSet(),
      };
      const r = rir();
      // `rir` è opzionale in §1.5: assente = non scelto. In una correzione, `rir: null` = rimosso
      // (merge-patch per-campo §2.3: il campo presente vince col suo valore).
      if (r !== null) data["rir"] = r;
      batch.push(
        makeEvent({
          type: "set.created",
          user_id,
          device_id,
          // contratto §2.2: il set porta TUTTE e due le chiavi di contesto (session_id + set_id)
          subject: [
            { key: "session_id", value: s.session_id },
            { key: "set_id", value: newId("set") },
          ],
          data,
        }),
      );
      await appendEvents(batch);
      await refresh();

      // D19: il rest parte DA SOLO dopo il salvataggio, durata dal template dell'esercizio appena
      // chiuso (non di quello successivo: è il recupero che stai facendo adesso)
      startRest(ex.rest_sec);

      // "Salva + avanti": prossimo set dell'esercizio, o prossimo esercizio a target raggiunto
      if (avanti) {
        const fatti = setsSessione().filter((x) => x.exercise_id === ex.exercise_id && !x.is_warmup).length;
        const g = giornoSessione();
        if (g && fatti >= ex.sets && esercizioIdx() < g.esercizi.length - 1) setEsercizioIdx(esercizioIdx() + 1);
      }
      preparaInput();
    } catch (err) {
      setNote(errorNote(err));
    } finally {
      setBusy(false);
    }
  }

  /** Valore carico coerente col tipo (§1.5): BW puro = 0, BW+ = kg aggiunti. */
  function caricoValore(): number {
    return tipoCarico() === "bodyweight" ? 0 : carico();
  }

  /**
   * D52: "correggi ultimo set" ≤2 tap (questo tap + "Salva correzione", ± solo se serve).
   * Carica nell'input lo stato CORRENTE del set (derivato dagli eventi, mai dal piano —
   * lezione wger #1289: re-seed dal dato salvato, mai dal piano = riga duplicata).
   */
  function caricaPerCorrezione(s: SetStato): void {
    setCorrezioneId(s.set_id);
    setRepsVal(s.reps);
    setCaricoVal(s.weight_kg);
    setTipoCarico(TIPI_CARICO.includes(s.load_type as TipoCarico) ? (s.load_type as TipoCarico) : "barbell");
    setRir(s.rir);
    setNoteSet(s.notes);
    setWarmup(s.is_warmup);
    setProposta(null);
    setReviewAperta(false); // la correzione avviene sull'input della Sessione
    setVista("sessione");
  }

  function correggiUltimo(): void {
    const sets = setsSessione();
    const ultimo = sets.length > 0 ? sets[sets.length - 1] : null;
    if (ultimo) caricaPerCorrezione(ultimo);
  }

  /** Fine sessione: `session.completed` (§1.4) con durata DERIVATA dagli eventi, non cronometrata. */
  async function confermaChiusura(): Promise<void> {
    if (busy()) return;
    setBusy(true);
    try {
      const s = attiva();
      if (!s) return;
      const batch: HyprEvent[] = [];
      const { user_id, device_id } = identita(batch);
      const creata = events().find((e) => e.type === "session.created" && e["session_id"] === s.session_id);
      const inizioMs = creata ? Date.parse(creata.timestamp) : Date.now();
      const durata = Math.max(1, Math.round((Date.now() - inizioMs) / 60_000));
      batch.push(
        makeEvent({
          type: "session.completed",
          user_id,
          device_id,
          subject: { key: "session_id", value: s.session_id },
          data: { status: "completed", duration_min: durata, notes: noteSessione() },
        }),
      );
      await appendEvents(batch);
      await refresh();
      setReviewAperta(false);
      setNoteSessione("");
      setRestFino(null);
      setVista("oggi");
    } catch (err) {
      setNote(errorNote(err));
    } finally {
      setBusy(false);
    }
  }

  // ---- export (D3 + D50: share-first adattivo, l'export NON si perde mai) ----
  async function esportaFile(file: File): Promise<void> {
    if (canShareFile(file)) {
      try {
        // invocato PRIMA di qualsiasi await: serve la transient activation del tap
        await navigator.share({ files: [file] });
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return; // annullato: silenzioso
        setNote(t("exportShareFailed"));
      }
    }
    // anche il fallback è coperto: un errore qui = status line, mai rejection silenziosa
    try {
      downloadText(file.name, await file.text(), file.type);
    } catch (err) {
      setNote(errorNote(err));
    }
  }

  function esportaSessione(session_id: string, formato: "jsonl" | "md"): void {
    const s = statoSessione(events(), session_id);
    if (!s) return;
    const file =
      formato === "jsonl"
        ? new File([jsonlSessione(events(), session_id)], nomeFileSessione(s, "jsonl"), { type: EXPORT_MIME })
        : new File([markdownSessione(events(), session_id)], nomeFileSessione(s, "md"), { type: "text/markdown" });
    void esportaFile(file);
  }

  function esportaTutto(): void {
    void esportaFile(jsonlFile(events()));
  }

  // ---- import (M2: backup/restore — export → installazione pulita → import → stato identico) ----
  let inputImport: HTMLInputElement | undefined;

  /**
   * Import JSONL = append con dedup per `event_id` (§2.3): l'append-only non si tocca, i
   * duplicati non si duplicano, i conflitti NON si risolvono in silenzio (vince il log locale,
   * il resoconto li conta). Gli eventi vecchi passano dalle MIGRAZIONI (upcast in lettura) col
   * contesto del log già noto, non del solo batch. D55: i tombstone si RICONOSCONO, si
   * conservano (append-only) e si dichiarano — semantica completa da M7. D56: un device senza
   * eventi propri adotta l'identità del file; identità in conflitto = mai fusione silenziosa.
   * REGOLA 1: ogni cosa che succede finisce nel resoconto — mai niente di silenzioso.
   */
  async function importaFile(file: File): Promise<void> {
    if (busy()) return;
    setBusy(true);
    try {
      const righe = (await file.text()).split("\n").filter((l) => l.trim() !== "");
      const eventi: HyprEvent[] = [];
      const scarti = new Map<string, number>(); // motivo → occorrenze: il PERCHÉ si ispeziona
      for (const riga of righe) {
        const p = parseEvent(riga);
        if (p.ok) eventi.push(p.event);
        else scarti.set(p.error, (scarti.get(p.error) ?? 0) + 1); // mai un'eccezione nuda (regola 1)
      }
      const scartati = [...scarti.values()].reduce((a, b) => a + b, 0);
      const tombstone = eventi.filter((e) => isTombstone(e.type)).length; // D55
      const { eventi: migrati, resoconto } = migraEventi(eventi, events()); // contesto = log locale
      const { nuovi, duplicati, conflitti, conflittiInterni } = eventiDaImportare(events(), migrati);
      const ident = esitoIdentita(migrati, events(), localStorage.getItem(USER_KEY)); // D56
      if (ident.tipo === "adotta") localStorage.setItem(USER_KEY, ident.user_id);
      if (nuovi.length > 0) await appendEvents(nuovi);
      await refresh();
      const extra: string[] = [];
      if (scartati > 0) {
        const motivi = [...scarti.entries()]
          .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
          .map(([m, n]) => `${m} ×${n}`)
          .join(", ");
        extra.push(`${t("importMotivi")}: ${motivi}`);
      }
      if (tombstone > 0) extra.push(`${tombstone} ${t("importTombstone")}`);
      if (conflittiInterni > 0) extra.push(`${conflittiInterni} ${t("importConflittiInterni")}`);
      if (resoconto.daV0 + resoconto.setOrfani + resoconto.orfaniIrrisolti + resoconto.placeholderNome > 0) {
        extra.push(
          `${t("importMigrazione")}: ${resoconto.daV0} ${t("migrazioneDaV0")} · ` +
            `${resoconto.setOrfani} ${t("migrazioneRicollegati")} · ` +
            `${resoconto.orfaniIrrisolti} ${t("migrazioneOrfani")} · ${resoconto.placeholderNome} ${t("migrazioneNomi")}`,
        );
      }
      if (ident.tipo === "adotta") extra.push(t("importIdentitaAdottata"));
      if (ident.tipo === "conflitto") extra.push(`${t("importIdentitaConflitto")} (${ident.identita.length})`);
      const base =
        `${t("importReport")}: ${nuovi.length} ${t("importNuovi")} · ${duplicati} ${t("importDuplicati")} · ` +
        `${conflitti.length} ${t("importConflitti")} · ${scartati} ${t("importScartati")}`;
      setNote(extra.length > 0 ? `${base} · ${extra.join(" · ")}` : base);
    } catch (err) {
      setNote(errorNote(err));
    } finally {
      setBusy(false);
    }
  }

  // ---- display helpers (deterministici, mai formattazioni magiche) ----
  const fmtMs = (v: number | null) => (v === null ? t("unknown") : `${v}ms`);
  const fmtMetric = (v: MetricDisplay, kind: "ms" | "cls"): string => {
    if (v === "waiting") return t("unknown");
    if (v === "na-engine") return t("naEngine");
    if (v === "na-iid") return t("naIid");
    if (v === "na-entries") return t("naEntries");
    return kind === "ms" ? `${v}ms` : String(v);
  };
  const etLabel = (): string => {
    const et = perfDiag().et;
    if (et === null) return t("no");
    return `${cap(et.entries)} ${t("etEntries")} (${cap(et.attributed)} iid)`;
  };
  // contatore a campo fisso: sopra 9999 si satura in "9999+" (la larghezza del campo-valore è
  // un invariante di layout, v. styles.css .metrica: D44 CLS)
  const cap = (n: number): string => (n > 9999 ? "9999+" : String(n));
  const fmtBytes = (b: number) => (b >= 1024 ** 3 ? `${(b / 1024 ** 3).toFixed(1)}GB` : `${Math.round(b / 1024)}KB`);

  // regola 1: la status line parla SOLO italiano. La diagnosi tecnica = il NOME dell'errore
  // (identificatore di piattaforma, indipendente dalla lingua), mai il messaggio grezzo.
  function errorNote(err: unknown): string {
    const name = err instanceof Error ? err.name : "Error";
    if (name === "QuotaExceededError") return t("errorQuota");
    if (name === "HyprStorageUnavailable") return t("storageError");
    if (name === "HyprStorageWrite") return t("saveFailed");
    return `${t("errorGeneric")} (${name})`;
  }

  const nomeEx = (exercise_id: string): string =>
    nomeEsercizioPerGiorno(giornoSessione(), exercise_id) ?? exercise_id;

  /** Riga di display del carico: unità esplicita per il bilanciere, simboli §1.5 per BW/BW+. */
  const caricoRiga = (s: SetStato): string =>
    s.load_type === "barbell" ? `${caricoLeggibile(s)} ${t("unitKg")}` : caricoLeggibile(s);

  // ---- helper del carico (label + ciclo tipo, §1.5) ----
  function labelCarico(): string {
    if (tipoCarico() === "barbell") return t("loadBarbell");
    return tipoCarico() === "bodyweight" ? t("loadBw") : t("loadBwPlus");
  }

  function cycleCarico(direzione: number): void {
    const i = TIPI_CARICO.indexOf(tipoCarico());
    const prossimo = TIPI_CARICO[(i + direzione + TIPI_CARICO.length) % TIPI_CARICO.length];
    if (prossimo) setTipoCarico(prossimo);
  }

  // metrica D51 (derivata) della sessione corrente: calcolata una volta, non per render
  const metrica = createMemo(() => metricaIdenticiUltimo(events(), attiva()?.session_id ?? ""));

  return (
    <div>
      <header>
        <Show
          when={vista() !== "sessione"}
          fallback={
            <h1>
              <button class="back" disabled={busy()} onClick={() => { setReviewAperta(false); setVista("oggi"); }}>
                {t("back")}
              </button>{" "}
              {t("sessione")}
            </h1>
          }
        >
          <h1>{vista() === "oggi" ? t("today") : vista() === "log" ? t("log") : t("account")}</h1>
        </Show>
        <p class="meta">
          {t("appTitle")} · {t("metaMilestone")} · {dataOggi()}
          <Show when={vista() === "sessione"}>
            {" · "}
            {t("rest")} {restLabel()}{" "}
            <button disabled={restFino() === null} onClick={() => setRestFino(null)}>
              {t("restSalta")}
            </button>{" "}
            <button onClick={() => setRestFino((restFino() ?? Date.now()) + 30_000)}>{t("restEstendi")}</button>
          </Show>
        </p>
      </header>
      <hr />

      {/* ---------- OGGI (wireframe §1) ---------- */}
      <Show when={vista() === "oggi"}>
        <section>
          <Show when={slotOggi()} fallback={<p class="meta">| {t("nessunTemplate")}</p>}>
            <p>
              {dataOggi()} · {slotOggi()!.oggi ? "" : `${t("prossimoSlot")}: `}
              {slotOggi()!.giorno.day_label} — {slotOggi()!.giorno.titolo}
            </p>
          </Show>
          <p class="meta">
            {t("rest")} {restLabel()}
          </p>
        </section>
        <hr />
        <section>
          <h2>{t("contesto")}</h2>
          <div class="ingresso">
            <span>{t("peso")}</span>
            <button aria-label={`${t("peso")} ${t("meno")}`} onClick={() => setPeso(String(Math.max(0, (Number.parseFloat(peso()) || 0) - 0.5)))}>−</button>
            <input
              type="text"
              inputmode="decimal"
              aria-label={t("peso")}
              value={peso()}
              onInput={(e) => setPeso(e.currentTarget.value)}
            />
            <button aria-label={`${t("peso")} ${t("piu")}`} onClick={() => setPeso(String((Number.parseFloat(peso()) || 0) + 0.5))}>+</button>
            <span class="meta">{t("unitKg")}</span>
          </div>
          <div class="ingresso">
            <span>{t("sonno")}</span>
            <button aria-label={`${t("sonno")} ${t("meno")}`} onClick={() => setSonno(String(Math.max(0, (Number.parseFloat(sonno()) || 0) - 0.5)))}>−</button>
            <input
              type="text"
              inputmode="decimal"
              aria-label={t("sonno")}
              value={sonno()}
              onInput={(e) => setSonno(e.currentTarget.value)}
            />
            <button aria-label={`${t("sonno")} ${t("piu")}`} onClick={() => setSonno(String((Number.parseFloat(sonno()) || 0) + 0.5))}>+</button>
            <span class="meta">{t("unitH")}</span>
          </div>
          <div class="ingresso" role="group" aria-label={t("energia")}>
            <span>{t("energia")}</span>
            <For each={[1, 2, 3, 4, 5]}>
              {(n) => (
                <button class={energia() === n ? "attivo" : ""} onClick={() => setEnergia(n)}>
                  {n}
                </button>
              )}
            </For>
            <span class="meta">{t("unitSu5")}</span>
          </div>
        </section>
        <hr />
        <section>
          <h2>{t("esercizi")}</h2>
          <table>
            <tbody>
              <For each={slotOggi()?.giorno.esercizi ?? []}>
                {(e, i) => (
                  <tr>
                    <td>
                      {i() + 1}. {e.nome}
                    </td>
                    <td>{prescrizione(e)}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </section>
        <hr />
        <section>
          <p>
            <button
              disabled={!ready() || busy()}
              onClick={() => {
                measureReaction();
                if (attiva()) {
                  preparaInput();
                  setVista("sessione");
                } else void iniziaSessione();
              }}
            >
              {attiva() ? t("riprendiSessione") : t("iniziaSessione")}
            </button>
          </p>
          <Show when={note()}>
            <p class="meta">{note()}</p>
          </Show>
        </section>
      </Show>

      {/* ---------- SESSIONE (wireframe §2) ---------- */}
      <Show when={vista() === "sessione"}>
        <section>
          <Show when={esercizioCorrente()}>
            <h2>{esercizioCorrente()!.nome}</h2>
            <p class="meta">
              {t("setCounter")} {setsSessione().filter((x) => x.exercise_id === esercizioCorrente()!.exercise_id && !x.is_warmup).length}/
              {esercizioCorrente()!.sets} · {t("rirTarget")}: {esercizioCorrente()!.rir_min === esercizioCorrente()!.rir_max ? esercizioCorrente()!.rir_min : `${esercizioCorrente()!.rir_min}-${esercizioCorrente()!.rir_max}`} ·{" "}
              {t("esercizioCounter")} {esercizioIdx() + 1}/{giornoSessione()?.esercizi.length ?? 1}{" "}
              <button disabled={esercizioIdx() === 0} onClick={() => { setEsercizioIdx(esercizioIdx() - 1); preparaInput(); }}>
                {t("prec")}
              </button>{" "}
              <button
                disabled={esercizioIdx() >= (giornoSessione()?.esercizi.length ?? 1) - 1}
                onClick={() => { setEsercizioIdx(esercizioIdx() + 1); preparaInput(); }}
              >
                {t("succ")}
              </button>
            </p>
          </Show>
          <p class="meta">
            {etichettaProposta() !== "" ? `| ${etichettaProposta()}` : ""}
          </p>
          <div class="ingresso">
            <span>{t("reps")}</span>
            <button aria-label={`${t("reps")} ${t("meno")}`} onClick={() => setRepsVal(reps() - 1)}>−</button>
            {/* D18: ± è la via veloce, la tastiera numerica è il fallback di precisione */}
            <input
              class="num"
              type="text"
              inputmode="numeric"
              aria-label={t("reps")}
              value={repsTesto()}
              onInput={(e) => digitaReps(e.currentTarget.value)}
            />
            <button aria-label={`${t("reps")} ${t("piu")}`} onClick={() => setRepsVal(reps() + 1)}>+</button>
          </div>
          <div class="ingresso">
            <span>{t("carico")}</span>
            <button aria-label={t("tipoPrec")} onClick={() => cycleCarico(-1)}>‹</button>
            <span class="meta">{labelCarico()}</span>
            <button aria-label={t("tipoSucc")} onClick={() => cycleCarico(1)}>›</button>
            <button
              aria-label={`${t("carico")} ${t("meno")}`}
              disabled={tipoCarico() === "bodyweight"}
              onClick={() => setCaricoVal(carico() - 2.5)}
            >
              −
            </button>
            <Show when={tipoCarico() !== "bodyweight"} fallback={<span class="num">{t("loadBw")}</span>}>
              <input
                class="num"
                type="text"
                inputmode="decimal"
                aria-label={t("carico")}
                value={caricoTesto()}
                onInput={(e) => digitaCarico(e.currentTarget.value)}
              />
            </Show>
            <button
              aria-label={`${t("carico")} ${t("piu")}`}
              disabled={tipoCarico() === "bodyweight"}
              onClick={() => setCaricoVal(carico() + 2.5)}
            >
              +
            </button>
            <span class="meta">{t("unitKg")}</span>
          </div>
          <div class="ingresso" role="group" aria-label={t("rir")}>
            <span>{t("rir")}</span>
            <For each={[0, 1, 2, 3, 4]}>
              {(n) => (
                <button class={rir() === n ? "attivo" : ""} onClick={() => setRir(n)}>
                  {n}
                </button>
              )}
            </For>
          </div>
          <div class="ingresso">
            <span>{t("noteSet")}</span>
            <input type="text" aria-label={t("noteSet")} value={noteSet()} onInput={(e) => setNoteSet(e.currentTarget.value)} />
          </div>
          <div class="ingresso">
            <button class={warmup() ? "attivo" : ""} onClick={() => setWarmup(!warmup())}>
              {t("warmupSet")}
            </button>
          </div>
          <Show when={esercizioCorrente()?.cue}>
            <p class="meta">
              {t("cue")}: {esercizioCorrente()!.cue}
            </p>
          </Show>
        </section>
        <hr />

        <Show
          when={!reviewAperta()}
          fallback={
            <section>
              <h2>{t("reviewTitle")}</h2>
              <table>
                <thead>
                  <tr>
                    <th>{t("esercizi")}</th>
                    <th>{t("setCounter")}</th>
                    <th>{t("reps")}</th>
                    <th>{t("carico")}</th>
                    <th>{t("rir")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  <For each={setsSessione()}>
                    {(s) => (
                      <tr>
                        <td>{nomeEx(s.exercise_id)}</td>
                        <td>{s.set_number}</td>
                        <td>{s.reps}</td>
                        <td>{caricoLeggibile(s)}</td>
                        <td>{s.rir ?? ""}</td>
                        <td>
                          <button onClick={() => caricaPerCorrezione(s)}>{t("correggi")}</button>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              <div class="ingresso">
                <span>{t("noteSessione")}</span>
                <input
                  type="text"
                  aria-label={t("noteSessione")}
                  value={noteSessione()}
                  onInput={(e) => setNoteSessione(e.currentTarget.value)}
                />
              </div>
              <p class="meta">
                | {t("metricaIdentici")}: {metrica().identici}/{metrica().confrontabili}
              </p>
              <p>
                <button disabled={busy()} onClick={() => void confermaChiusura()}>
                  {t("confermaChiudi")}
                </button>{" "}
                <button disabled={busy()} onClick={() => setReviewAperta(false)}>
                  {t("annulla")}
                </button>
              </p>
            </section>
          }
        >
          <section>
            <h2>{t("setCompletati")}</h2>
            <table>
              <tbody>
                <For each={setsSessione().filter((x) => x.exercise_id === esercizioCorrente()?.exercise_id)}>
                  {(s) => (
                    <tr>
                      <td>
                        {s.set_number}. {s.reps} × {caricoRiga(s)}
                        {s.rir !== null ? ` · ${t("rir")} ${s.rir}` : ""}
                        {s.is_warmup ? ` · ${t("markWarmup")}` : ""} {s.corretto ? `· ${t("markCorretto")}` : t("markOk")}
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
            <p>
              <button disabled={!ready() || busy()} onClick={() => { measureReaction(); void salva(false); }}>
                {correzioneId() !== null ? t("salvaCorrezione") : t("salvaSet")}
              </button>{" "}
              <Show
                when={correzioneId() === null}
                fallback={
                  <button disabled={busy()} onClick={() => { setCorrezioneId(null); preparaInput(); }}>
                    {t("annulla")}
                  </button>
                }
              >
                <button disabled={!ready() || busy()} onClick={() => void salva(true)}>
                  {t("salvaAvanti")}
                </button>
              </Show>
            </p>
            <p>
              <button disabled={!ready() || busy() || setsSessione().length === 0} onClick={correggiUltimo}>
                {t("correggiUltimo")}
              </button>{" "}
              <button disabled={!ready() || busy()} onClick={() => setReviewAperta(true)}>
                {t("terminaSessione")}
              </button>
            </p>
            <Show when={note()}>
              <p class="meta">{note()}</p>
            </Show>
          </section>
        </Show>
      </Show>

      {/* ---------- LOG (wireframe §7: storico sessioni + export) ---------- */}
      <Show when={vista() === "log"}>
        <section>
          <h2>{t("log")}</h2>
          <Show when={elencoSessioni().length > 0} fallback={<p class="meta">| {t("logVuoto")}</p>}>
            <table>
              <thead>
                <tr>
                  <th>{t("colData")}</th>
                  <th>{t("colGiorno")}</th>
                  <th>{t("colSet")}</th>
                  <th>{t("colStato")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <For each={[...elencoSessioni()].reverse()}>
                  {(s) => (
                    <tr>
                      <td>{s.date}</td>
                      <td>{s.day_label}</td>
                      <td>{statiSet(events(), s.session_id).length}</td>
                      <td>{s.status === "in_progress" ? t("statoInCorso") : t("statoCompletata")}</td>
                      <td>
                        <button onClick={() => esportaSessione(s.session_id, "jsonl")}>{t("exportJsonlShort")}</button>{" "}
                        <button onClick={() => esportaSessione(s.session_id, "md")}>{t("exportMdShort")}</button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </Show>
        </section>
      </Show>

      {/* ---------- ACCOUNT (M1 minimo: nome utente + export completo + eventi ispezionabili) ---------- */}
      <Show when={vista() === "account"}>
        <section>
          <h2>{t("account")}</h2>
          {/* D54: edit inline del nome visualizzato (wireframe §7: "tap su profilo → edit inline"),
              mai un onboarding: default esplicito, si cambia quando vuoi in ≤2 tap */}
          <div class="ingresso">
            <span>{t("nome")}</span>
            <input
              type="text"
              aria-label={t("nome")}
              placeholder={t("defaultDisplayName")}
              value={nomeUtente()}
              onInput={(e) => setNomeUtente(e.currentTarget.value)}
            />
            <button disabled={!ready() || busy()} onClick={() => void salvaNome()}>
              {t("salvaNome")}
            </button>
          </div>
          <p>
            <button disabled={!ready() || events().length === 0} onClick={esportaTutto}>
              {t("esportaJsonl")}
            </button>{" "}
            {/* M2: import = ripristino/secondo device; funziona anche su DB vuoto (è il punto) */}
            <button disabled={!ready() || busy()} onClick={() => inputImport?.click()}>
              {t("importaJsonl")}
            </button>
            <input
              ref={inputImport}
              type="file"
              accept=".jsonl,.txt,application/x-ndjson,application/json"
              aria-label={t("importaJsonl")}
              hidden
              onChange={(e) => {
                const f = e.currentTarget.files?.[0];
                e.currentTarget.value = ""; // stesso file selezionabile due volte (riprova import)
                if (f) void importaFile(f);
              }}
            />
          </p>
        </section>
        <hr />
        <section>
          <h2>{t("events")}</h2>
          <table>
            <thead>
              <tr>
                <th>{t("colType")}</th>
                <th>{t("colTime")}</th>
                <th>{t("colId")}</th>
              </tr>
            </thead>
            <tbody>
              <For each={events().slice(-8).reverse()}>
                {(e) => (
                  <tr>
                    <td>{e.type}</td>
                    <td>{e.timestamp}</td>
                    <td>{e.event_id}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={note()}>
            <p class="meta">{note()}</p>
          </Show>
        </section>
      </Show>

      <div class="footer" ref={footerEl}>
        <Show when={vista() !== "sessione"}>
          <nav class="nav">
            <button class={vista() === "oggi" ? "attivo" : ""} onClick={() => setVista("oggi")}>
              {t("today")}
            </button>
            <button class={vista() === "log" ? "attivo" : ""} onClick={() => setVista("log")}>
              {t("log")}
            </button>
            <button class={vista() === "account" ? "attivo" : ""} onClick={() => setVista("account")}>
              {t("account")}
            </button>
          </nav>
        </Show>
        {/* Status line = tabella di metriche a campi-valore di larghezza fissa (v. styles.css
            .metrica): il popolamento asincrono dei valori non deve MAI spostare le righe —
            D44 CLS ≤ 0.05 (gate G-perf; misurato con scripts/probe-cls-sources.mjs) */}
        <p class="status">
          <span class="metrica">
            {t("statusPersisted")}: <span class="val">{status() ? (status()!.persisted ? t("yes") : t("no")) : t("unknown")}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("statusStorage")}: <span class="val val-larga">{storageMode()}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("statusSpace")}: <span class="val val-larga">{dataBytes() != null ? fmtBytes(dataBytes()!) : t("unknown")}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("statusQuota")}: <span class="val val-larga">{status()?.quota != null ? fmtBytes(status()!.quota!) : t("unknown")}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("statusEvents")}: <span class="val val-larga">{events().length}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("statusSchema")}: <span class="val">v{SCHEMA_VERSION}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("perfLcp")} <span class="val val-maxima">{fmtMetric(lcpDisplay(PERF_SUPPORT.lcp, perfNumbers().lcp_ms), "ms")}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("perfInp")} <span class="val val-maxima">{fmtMetric(
              inpDisplay({
                supported: PERF_SUPPORT.inp,
                valueMs: perfNumbers().inp_ms,
                entries: perfDiag().et?.entries ?? 0,
                attributed: perfDiag().et?.attributed ?? 0,
                tapPainted: perfDiag().tapPainted,
              }),
              "ms",
            )}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("perfCls")} <span class="val val-maxima">{fmtMetric(clsDisplay(PERF_SUPPORT.cls, perfNumbers().cls), "cls")}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("perfRt")} <span class="val val-larga">{fmtMs(perfRt())}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("perfEt")} <span class="val val-et">{etLabel()}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("statusSw")}: <span class="val">{swOn()}</span>
          </span>{" "}
          ·{" "}
          <span class="metrica">
            {t("statusVersion")}: <span class="val val-larga">{BUILD_VERSION}</span>
          </span>
        </p>
      </div>
    </div>
  );
}
